/**
 * Top-of-page stack for the live menu:
 *  - Hero strip (product count + View Specials CTA + StoreSwitcher)
 *  - Sticky toolbar (search + sort + clear + mobile-filters toggle)
 *  - Category and subcategory chip strips
 *
 * Pure presentation — all state lives in the parent (StoreMenu).
 */
import { Link } from "react-router-dom";
import { Tag, SlidersHorizontal, X } from "lucide-react";

const SORT_OPTIONS = [
    { key: "POPULAR", dir: "DESC", label: "Most popular" },
    { key: "PRICE", dir: "ASC", label: "Price: low → high" },
    { key: "PRICE", dir: "DESC", label: "Price: high → low" },
    { key: "NAME", dir: "ASC", label: "Name: A → Z" },
    { key: "POTENCY", dir: "DESC", label: "Potency: high → low" },
];
export { SORT_OPTIONS };

export default function MenuHeader({
    storeSlug,
    store,
    menu,
    visibleCount,
    needsClientFilter,
    subcategory,
    category,
    specialsCount,
    onOpenMobileFilters,
    activeFilterCount,
    sortValue,
    onSortChange,
    onClearAll,
    categories,
    onCategory,
    subcategories,
    onSubcategory,
}) {
    return (
        <>
            <section className="border-b border-zen-ink/8 bg-zen-bg">
                <div className="max-w-none mx-auto px-3 sm:px-5 pt-5 pb-3 sm:pt-6 sm:pb-4">
                    <div className="flex flex-wrap items-end justify-between gap-3">
                        <div className="flex-1 min-w-0">
                            <p className="text-[11px] uppercase tracking-[0.35em] text-zen-olive mb-1.5">
                                Live Menu · {store?.short_name || "Loading…"}
                            </p>
                            <h1 className="font-serif text-2xl sm:text-3xl font-light text-zen-ink leading-[1.05] tracking-tight">
                                {(needsClientFilter
                                    ? visibleCount
                                    : menu?.productsCount)?.toLocaleString() || "—"}{" "}
                                products
                                {subcategory
                                    ? ` in ${subcategory.replace(/_/g, " ").toLowerCase()}`
                                    : category
                                      ? ` in ${category.replace(/_/g, " ").toLowerCase()}`
                                      : " on shelf"}
                                ,{" "}
                                <span className="italic text-zen-olive">live.</span>
                            </h1>
                        </div>
                        <Link
                            to={`/menu/${storeSlug}/specials`}
                            data-testid="menu-specials-link"
                            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-zen-ink text-zen-cream text-[11px] uppercase tracking-[0.25em] font-medium hover:bg-zen-olive-dark transition-colors whitespace-nowrap"
                        >
                            <Tag size={12} /> View specials
                            {specialsCount > 0 && (
                                <span className="ml-1 px-1.5 py-0.5 bg-red-600 text-white text-[10px] tracking-normal">
                                    {specialsCount}
                                </span>
                            )}
                        </Link>
                    </div>
                </div>
            </section>

            {/* Sticky toolbar — Category chips + Sort + Clear all in one
                row so they remain pinned to the top together when scrolling
                a long product grid. Mobile-Filters toggle sits on the left.
                Subcategory ("Refine") chips fold into the sticky block when
                a category is selected. The live-search input is intentionally
                NOT here; shoppers should use the always-visible global search
                in the site header so we have one canonical search surface. */}
            <section className="sticky top-[72px] z-40 bg-zen-bg/85 backdrop-blur-xl border-b border-zen-ink/10">
                <div className="max-w-none mx-auto px-3 sm:px-5 py-3 flex items-center gap-3">
                    <button
                        onClick={onOpenMobileFilters}
                        data-testid="menu-mobile-filters-toggle"
                        className="lg:hidden shrink-0 inline-flex items-center gap-2 px-3 py-2 border border-zen-ink/15 text-zen-ink text-[11px] uppercase tracking-[0.22em] hover:border-zen-ink/40"
                    >
                        <SlidersHorizontal size={14} />
                        Filters
                        {activeFilterCount > 0 && (
                            <span className="bg-zen-olive text-zen-bg text-[10px] w-5 h-5 inline-flex items-center justify-center rounded-full">
                                {activeFilterCount}
                            </span>
                        )}
                    </button>

                    {/* Inline category chip strip — takes the remaining
                        horizontal space and scrolls if the chips overflow. */}
                    <div
                        data-testid="menu-category-chips"
                        className="flex items-center gap-2 overflow-x-auto flex-1 min-w-0"
                    >
                        <button
                            onClick={() => onCategory("")}
                            data-testid="menu-chip-cat-all"
                            className={`shrink-0 px-3 py-1.5 text-[11px] uppercase tracking-[0.22em] border transition-colors ${
                                !category
                                    ? "bg-zen-ink text-zen-cream border-zen-ink"
                                    : "border-zen-line text-zen-ink-soft hover:border-zen-ink/40"
                            }`}
                        >
                            All
                        </button>
                        {categories.map((c) => (
                            <button
                                key={c.slug}
                                onClick={() => onCategory(c.slug)}
                                data-testid={`menu-chip-cat-${c.slug.toLowerCase()}`}
                                className={`shrink-0 px-3 py-1.5 text-[11px] uppercase tracking-[0.22em] border transition-colors ${
                                    category === c.slug
                                        ? "bg-zen-ink text-zen-cream border-zen-ink"
                                        : "border-zen-line text-zen-ink-soft hover:border-zen-ink/40"
                                }`}
                            >
                                {c.name}
                            </button>
                        ))}
                    </div>

                    <div className="shrink-0 hidden sm:flex items-center gap-2 sm:gap-3">
                        <label className="hidden sm:inline text-[10px] uppercase tracking-[0.22em] text-zen-ink-soft">
                            Sort
                        </label>
                        <select
                            data-testid="menu-sort-select"
                            value={sortValue}
                            onChange={(e) => onSortChange(e.target.value)}
                            className="bg-white border border-zen-ink/15 hover:border-zen-ink/30 focus:border-zen-olive text-zen-ink text-sm px-2 sm:px-3 py-2 focus:outline-none transition-colors max-w-[140px] sm:max-w-none"
                        >
                            {SORT_OPTIONS.map((o) => (
                                <option key={`${o.key}:${o.dir}`} value={`${o.key}:${o.dir}`}>
                                    {o.label}
                                </option>
                            ))}
                        </select>
                        {activeFilterCount > 0 && (
                            <button
                                onClick={onClearAll}
                                data-testid="menu-clear-filters"
                                className="text-[11px] uppercase tracking-[0.22em] text-zen-ink-soft hover:text-zen-ink inline-flex items-center gap-1"
                            >
                                Clear <X size={12} />
                            </button>
                        )}
                    </div>
                </div>

                {/* Subcategory ("Refine") strip stays inside the sticky block
                    so when a category is selected the refinement chips ride
                    along with the sort row. */}
                {category && subcategories.length > 0 && (
                    <div className="max-w-none mx-auto px-3 sm:px-5 pb-3">
                        <div
                            data-testid="menu-subcategory-chips"
                            className="flex items-center gap-2 overflow-x-auto pt-2 border-t border-zen-line"
                        >
                            <span className="shrink-0 text-[10px] uppercase tracking-[0.28em] text-zen-ink-soft pr-2">
                                Refine
                            </span>
                            <button
                                onClick={() => onSubcategory("")}
                                data-testid="menu-chip-sub-all"
                                className={`shrink-0 px-3 py-1 text-[10px] uppercase tracking-[0.2em] border transition-colors ${
                                    !subcategory
                                        ? "bg-zen-olive border-zen-olive text-zen-cream"
                                        : "border-zen-line text-zen-ink-soft hover:border-zen-olive"
                                }`}
                            >
                                All
                            </button>
                            {subcategories.map((s) => (
                                <button
                                    key={s}
                                    onClick={() => onSubcategory(s)}
                                    data-testid={`menu-chip-sub-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                    className={`shrink-0 px-3 py-1 text-[10px] uppercase tracking-[0.2em] border transition-colors ${
                                        subcategory === s
                                            ? "bg-zen-olive border-zen-olive text-zen-cream"
                                            : "border-zen-line text-zen-ink-soft hover:border-zen-olive"
                                    }`}
                                >
                                    {s.replace(/_/g, " ")}
                                </button>
                            ))}
                        </div>
                    </div>
                )}
            </section>
        </>
    );
}
